import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ShieldCheck, HandWaving, ArrowRight, MagnifyingGlass, Stethoscope, Star, Heart, Baby, Brain, Eye, Ear, Tooth, Drop, GenderFemale, Pill, Syringe, Bone, Thermometer, FirstAid, Microscope, CaretLeft, CaretRight, CheckCircle } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import { Button } from "../../components/ui/button";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import DEFAULT_HOME_CONTENT from "@/data/homepageContent";
import { loadManagedSpecialtyNames, SPECIALTIES } from "@/lib/specialties";
import { useBranding } from "@/hooks/useBranding";

const SPECIALTY_ICONS = {
  "General Physician": Stethoscope,
  "Cardiology": Heart,
  "Dermatology": Drop,
  "Orthopedics": Bone,
  "Pediatrics": Baby,
  "Neurology": Brain,
  "Ophthalmology": Eye,
  "ENT (Ear, Nose, Throat)": Ear,
  "Gynecology & Obstetrics": GenderFemale,
  "Gynaecology & Obstetrics": GenderFemale,
  "Gastroenterology": Pill,
  "Pulmonology": Thermometer,
  "Psychiatry": Brain,
  "Urology": Pill,
  "Endocrinology": Drop,
  "Nephrology": Drop,
  "Oncology": Microscope,
  "Rheumatology": Bone,
  "Dental & Maxillofacial Surgery": Tooth,
  "Dental Surgery": Tooth,
  "Orthodontics": Tooth,
  "Physiotherapy": Bone,
  "Psychology": Brain,
  "General Surgery": Syringe,
  "Emergency Medicine": FirstAid,
  "Family Medicine": Stethoscope,
  "Internal Medicine": Stethoscope,
  "Cardiac Surgery": Heart,
  "Neurosurgery": Brain,
  "Pediatric Surgery": Baby,
  "Plastic Surgery": Syringe,
};

function getSpecialtyIcon(name) {
  return SPECIALTY_ICONS[name] || Stethoscope;
}

export default function Landing() {
  const branding = useBranding();
  const [featured, setFeatured] = useState([]);
  const [homeContent, setHomeContent] = useState(DEFAULT_HOME_CONTENT);
  const [specialties, setSpecialties] = useState(SPECIALTIES);
  const specialtiesScrollRef = useRef(null);
  const featuredScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [canFeaturedScrollLeft, setCanFeaturedScrollLeft] = useState(false);
  const [canFeaturedScrollRight, setCanFeaturedScrollRight] = useState(false);

  useEffect(() => {
    api.get("/doctors?featured=true").then(({ data }) => setFeatured(data.doctors || [])).catch(() => {});
    api.get("/content/homepage").then(({ data }) => setHomeContent({ ...DEFAULT_HOME_CONTENT, ...data })).catch(() => {});
    loadManagedSpecialtyNames({ featuredOnly: true }).then(setSpecialties);
  }, []);

  const scrollSpecialties = (dir) => {
    const el = specialtiesScrollRef.current;
    if (!el) return;
    const amount = 340;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  const checkScroll = () => {
    const el = specialtiesScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scrollFeatured = (dir) => {
    const el = featuredScrollRef.current;
    if (!el) return;
    const amount = 330;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  const checkFeaturedScroll = () => {
    const el = featuredScrollRef.current;
    if (!el) return;
    setCanFeaturedScrollLeft(el.scrollLeft > 10);
    setCanFeaturedScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const timer = setTimeout(checkFeaturedScroll, 100);
    return () => clearTimeout(timer);
  }, [featured.length]);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar platformName={branding.product_name} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="workspace-page pt-6 sm:pt-8 pb-8 sm:pb-10">
          <div className="flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-xs uppercase tracking-[0.16em] text-secondary-foreground mb-4 sm:mb-5 w-fit">
            <ShieldCheck size={14} weight="duotone" />
            {homeContent.hero_badge}
          </div>
          <h1 className="font-display font-extrabold text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            {homeContent.hero_title}<br />
            <span className="text-[#C57053]">{homeContent.hero_highlight}</span>
          </h1>
          <p className="mt-4 sm:mt-5 text-base text-muted-foreground max-w-xl leading-relaxed">
            {homeContent.hero_body}
          </p>

          <div className="mt-6 sm:mt-7 flex flex-wrap justify-center gap-3 sm:gap-4">
            <Link to="/doctors" data-testid="hero-find-btn" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto rounded-2xl h-12 px-7 border-primary/30 text-primary bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary text-base">
                <MagnifyingGlass size={18} weight="duotone" className="mr-2" /> {homeContent.primary_button_label}
              </Button>
            </Link>
            <Link to="/apply" data-testid="hero-apply-btn" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto rounded-2xl h-12 px-7 text-base">
                {homeContent.doctor_button_label}
              </Button>
            </Link>
          </div>

        </div>
      </section>

      {/* Specialties */}
      <section className="border-t border-border bg-muted/30">
        <div className="workspace-page py-10 sm:py-12">
          <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{homeContent.specialties_eyebrow}</div>
              <h2 className="font-display font-bold text-foreground text-3xl sm:text-4xl tracking-tight">
                {homeContent.specialties_title}
              </h2>
            </div>
            <Link to="/doctors" className="text-sm text-foreground underline underline-offset-4">
              {homeContent.specialties_link_label} →
            </Link>
          </div>
          <div className="relative">
            {canScrollLeft && (
              <button
                type="button"
                onClick={() => scrollSpecialties("left")}
                className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-xl bg-card border border-border shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-colors"
                aria-label="Scroll left"
              >
                <CaretLeft size={18} weight="bold" />
              </button>
            )}
            {canScrollRight && (
              <button
                type="button"
                onClick={() => scrollSpecialties("right")}
                className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-xl bg-card border border-border shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-colors"
                aria-label="Scroll right"
              >
                <CaretRight size={18} weight="bold" />
              </button>
            )}
            <div
              ref={specialtiesScrollRef}
              onScroll={checkScroll}
              className="flex gap-4 overflow-x-auto scroll-smooth pb-2 -mx-2 px-2 hide-scrollbar"
            >
              {specialties.map((item) => {
                const Icon = getSpecialtyIcon(item);
                return (
                  <Link
                    key={item}
                    to={`/doctors?specialty=${encodeURIComponent(item)}`}
                    className="group relative flex flex-col items-center gap-3 rounded-xl border border-border bg-card/85 p-4 min-w-[150px] w-[150px] overflow-hidden hover:border-primary/35 hover:bg-card hover:-translate-y-0.5 hover:shadow-md transition-all shrink-0"
                  >
                    <div className="absolute inset-x-5 top-0 h-px bg-primary/20 group-hover:bg-primary/55 transition-colors" />
                    <div className="relative w-14 h-14 rounded-xl bg-muted/70 border border-border flex items-center justify-center text-primary/85 group-hover:bg-primary/10 group-hover:border-primary/25 group-hover:text-primary transition-colors">
                      <Icon size={30} weight="duotone" />
                    </div>
                    <span className="relative text-sm font-bold text-foreground text-center leading-tight">{item}</span>
                  </Link>
                );
              })}
              <Link
                to="/doctors"
                className="group flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-card/70 p-5 min-w-[142px] w-[142px] hover:border-primary/40 hover:bg-card hover:-translate-y-0.5 hover:shadow-md transition-all shrink-0"
              >
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <ArrowRight size={28} weight="bold" />
                </div>
                <span className="text-sm font-bold text-muted-foreground text-center">See all</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured doctors */}
      {featured.length > 0 && (
        <section className="border-t border-border bg-muted/40">
          <div className="workspace-page py-10 sm:py-12">
            <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{homeContent.featured_eyebrow}</div>
                <h2 className="font-display font-bold text-foreground text-3xl sm:text-4xl tracking-tight max-w-xl">
                  {homeContent.featured_title}
                </h2>
              </div>
              <Link to="/doctors" className="text-sm text-foreground underline underline-offset-4">
                {homeContent.featured_link_label} →
              </Link>
            </div>

            <div className="relative" data-testid="featured-doctors">
              {canFeaturedScrollLeft && (
                <button
                  type="button"
                  onClick={() => scrollFeatured("left")}
                  className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-xl bg-card border border-border shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-colors"
                  aria-label="Scroll featured doctors left"
                >
                  <CaretLeft size={18} weight="bold" />
                </button>
              )}
              {canFeaturedScrollRight && (
                <button
                  type="button"
                  onClick={() => scrollFeatured("right")}
                  className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-xl bg-card border border-border shadow-md flex items-center justify-center text-foreground hover:bg-muted transition-colors"
                  aria-label="Scroll featured doctors right"
                >
                  <CaretRight size={18} weight="bold" />
                </button>
              )}
              <div
                ref={featuredScrollRef}
                onScroll={checkFeaturedScroll}
                className="flex gap-4 overflow-x-auto scroll-smooth pb-2 -mx-2 px-2 hide-scrollbar"
              >
                {featured.map((d) => (
                  <Link
                    key={d.id}
                    to={`/doctors/${d.id}`}
                    className="group w-[268px] sm:w-[292px] shrink-0 rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/25 hover:-translate-y-0.5 hover:shadow-md transition-all"
                    data-testid={`featured-doctor-${d.id}`}
                  >
                    <div className="relative flex justify-center pt-7 pb-4">
                      <div className="w-28 h-28 rounded-full overflow-hidden bg-muted border border-border shadow-sm">
                        {d.photo_url ? (
                          <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Stethoscope size={42} weight="duotone" />
                          </div>
                        )}
                      </div>
                      {d.clinic_open !== false && (
                        <span className="absolute bottom-4 right-[34%] w-4 h-4 rounded-full bg-emerald-500 border-2 border-card" />
                      )}
                    </div>
                    <div className="px-5 pb-6">
                      <div className="flex items-center justify-center gap-1.5">
                        <h3 className="font-display font-bold text-lg text-foreground text-center truncate">
                          {d.name}
                        </h3>
                        {d.verified && <CheckCircle size={16} weight="fill" className="text-blue-500 shrink-0" />}
                      </div>
                      <div className="mt-2 flex justify-center">
                        <span className="max-w-full rounded-full bg-accent/10 px-3 py-1 text-xs font-bold text-accent truncate">
                          {d.specialty}
                        </span>
                      </div>
                      <div className="mt-5 space-y-2 text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Stethoscope size={14} weight="duotone" className="shrink-0" />
                          <span className="truncate">{d.clinic_name || "Clinic details"}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-emerald-600">
                          <Clock size={14} weight="duotone" className="shrink-0" />
                          <span className="truncate">{d.today_status?.label || "Check availability"}</span>
                        </div>
                      </div>
                      <div className="my-5 h-px bg-border" />
                      <div className="flex items-center justify-between gap-3">
                        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                          <Star size={14} weight="duotone" /> {d.years_experience}+ yrs
                        </span>
                        <span className="inline-flex max-w-[38px] items-center gap-1.5 overflow-hidden rounded-full bg-primary px-3 py-2 text-sm font-bold text-primary-foreground transition-all duration-300 group-hover:max-w-[132px] group-hover:bg-[#1a3025]">
                          <ArrowRight size={16} weight="bold" className="shrink-0" />
                          <span className="whitespace-nowrap opacity-0 transition-opacity duration-200 group-hover:opacity-100">Book Now</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats banner */}
      {homeContent.green_strip_enabled && (
        <section className="bg-[#1a3c34] py-12 sm:py-16 px-4 sm:px-6">
          <div className="workspace-page grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              [homeContent.stats_one_value, homeContent.stats_one_label],
              [homeContent.stats_two_value, homeContent.stats_two_label],
              [homeContent.stats_three_value, homeContent.stats_three_label],
              [homeContent.stats_four_value, homeContent.stats_four_label],
            ].map(([value, label], index) => (
              <div key={index} className="min-h-[112px] sm:min-h-0 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-5 sm:border-0 sm:bg-transparent sm:p-0 text-center flex flex-col items-center justify-center">
                <div className="text-3xl sm:text-5xl font-bold text-white font-display tracking-tight">{value}</div>
                <div className="text-[10px] sm:text-sm font-bold text-white/55 tracking-[0.14em] sm:tracking-widest uppercase mt-2 leading-snug">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="border-t border-border">
        <div className="workspace-page py-14 sm:py-16">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">How it works</div>
              <h2 className="font-display font-bold text-foreground text-4xl lg:text-5xl tracking-tight max-w-xl">
                Three steps. No phone calls.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            <Step n="01" icon={<MagnifyingGlass size={24} weight="duotone" />} title="Find your doctor" copy="Search by specialty, browse verified profiles, fees and timings." />
            <Step n="02" icon={<Clock size={24} weight="duotone" />} title="Reserve a token" copy="Tap once and you're slotted into today's clinic queue." />
            <Step n="03" icon={<HandWaving size={24} weight="duotone" />} title="Walk in when called" copy="Track the queue live and arrive only when it's your turn." />
          </div>
        </div>
      </section>

      {/* For doctors CTA */}
      <section className="bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="workspace-page py-14 sm:py-16 relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-3">For doctors</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Run a calmer clinic.<br />Bring your patients online.
            </h2>
            <p className="mt-5 max-w-xl text-primary-foreground/85 leading-relaxed">
              List your practice, set your fee and timings, and let patients reserve walk-in tokens from home.
              Approval is fast and your dashboard is live the moment you're verified.
            </p>
            <div className="mt-8">
              <Link to="/apply" data-testid="cta-apply-btn">
                <Button className="rounded-full h-12 px-7 bg-accent text-accent-foreground hover:bg-[#a85a40]">
                  Apply to join <ArrowRight size={18} weight="bold" className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
          <ul className="space-y-4 text-primary-foreground/90 text-base">
            {[
              "Custom queue per doctor with FCFS tokens",
              "One-click 'Complete & call next' for fast turnover",
              "Live ETA so patients walk in just on time",
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <ShieldCheck size={20} weight="duotone" className="mt-0.5 text-accent" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Footer platformName={branding.product_name} />
    </div>
  );
}

function Step({ n, icon, title, copy }) {
  return (
    <div className="bg-card p-8 hover:bg-muted/40 transition-colors">
      <div className="flex items-center justify-between mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{n}</div>
        <div className="text-primary">{icon}</div>
      </div>
      <h3 className="font-display font-bold text-2xl text-foreground tracking-tight">{title}</h3>
      <p className="mt-3 text-muted-foreground leading-relaxed">{copy}</p>
    </div>
  );
}
